// Pure "date of test" suggestion for the patient Upload Report page — no pdf.js import, so it is
// unit-testable in Jest. The caller extracts the text (pdfText.js) and passes it in.
//
// The date patterns follow the backend's TrendTextDetector (DATE regex and iso()): day first, a month
// as a number or a name, 2-digit years read as 20xx, and anything that is not a real calendar date is
// dropped. They are tightened in two ways: a numeric date must use the same separator twice with no
// spaces (so a reference range like "4.5 - 11.0" is never read as 04 May 2011), and there is no regex
// lookbehind (older Safari cannot parse it, and a parse error here would break the page).
//
// The result is only ever a suggestion the patient can change, so whenever the report is ambiguous
// (two different collection dates, nothing labelled and several dates in the text) this returns null.

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH = '(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\\.?(?![a-z])';
const ORDINAL = '(?:st|nd|rd|th)?';

// Each pattern starts with a captured prefix (instead of a lookbehind); `order` maps the remaining
// groups to [day, month, year].
const DATE_PATTERNS = [
  // 2026-01-04 (optionally followed by T10:30)
  { re: /(^|[^\d./-])(\d{4})-(\d{1,2})-(\d{1,2})(?!\d)/gi, order: [4, 3, 2] },
  // 04/01/2026, 04-01-2026, 04.01.2026, 04/01/26 — the same separator twice
  { re: /(^|[^\d./:-])(\d{1,2})([/.-])(\d{1,2})\3(\d{4}|\d{2})(?!\d|[./-]\d)/gi, order: [2, 4, 5] },
  // 04-Jan-2026, 04/Jan/2026, 4 Jan 2026, 04 January 2026, 4th Jan, 2026
  { re: new RegExp(`(^|[^\\w])(\\d{1,2})${ORDINAL}[\\s/.,-]*${MONTH}[\\s/.,-]*(\\d{4}|\\d{2})(?!\\d)`, 'gi'), order: [2, 3, 4] },
  // Jan 4, 2026
  { re: new RegExp(`(^|[^\\w])${MONTH}\\s*(\\d{1,2})${ORDINAL},?\\s+(\\d{4})(?!\\d)`, 'gi'), order: [3, 2, 4] },
];

// Highest priority first. Printed / generated dates are only a last resort.
const LABEL_GROUPS = [
  /\b(?:sample\s*collect(?:ed|ion)\s*(?:date|on|at)?|collected\s*(?:on|at|date)?|collection\s*(?:date|on|at)|date\s*of\s*collection|sample\s*date|drawn\s*on)\b/gi,
  /\b(?:(?:sample\s*)?received\s*(?:on|at|date)?|registered\s*(?:on|at)|registration\s*(?:date|on|at))\b/gi,
  /\b(?:reported\s*(?:on|at|date)?|report\s*date|date\s*of\s*report|approved\s*(?:on|at|date)?)\b/gi,
  /(?:^|\s{2,}|\|)\s*(date)\s*[:-]/gi,
  /\b(?:printed\s*(?:on|at|date)?|print\s*date|generated\s*(?:on|at|date)?)\b/gi,
];

// Columns that carry the patient's birth date or age, never the test date. "Age" is a whole word only
// (not Average, Package, Dosage).
const BIRTH_COLUMN = /\b(?:d\.?\s*o\.?\s*b\b|date\s*of\s*birth|birth\s*date|age\b)/i;
const TEXT_FALLBACK_LINES = 40;
const MAX_AGE_YEARS = 20;

const pad = (n) => String(n).padStart(2, '0');

/** Local calendar date as YYYY-MM-DD (never toISOString, which is UTC and can be a day off in India). */
export const toLocalIsoDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** "2026-01-04" → "04 Jan 2026" (en-IN), built from a local Date. '' for anything else. */
export const formatSuggestion = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return '';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    .toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Day-first parts → YYYY-MM-DD, or null when it is not a real calendar date (port of TrendTextDetector.iso). */
const iso = (day, month, year) => {
  const mo = /^\d+$/.test(month) ? Number(month) : MONTHS.indexOf(month.slice(0, 3).toLowerCase()) + 1;
  let y = Number(year);
  if (y < 100) y += 2000;
  const d = Number(day);
  if (mo < 1 || mo > 12 || d < 1) return null;
  const daysInMonth = new Date(y, mo, 0).getDate();
  return d > daysInMonth ? null : `${y}-${pad(mo)}-${pad(d)}`;
};

/** Every real calendar date on a line: [{ iso, index }], in reading order, overlapping matches dropped. */
const datesIn = (line) => {
  const found = [];
  for (const { re, order } of DATE_PATTERNS) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(line)) !== null) {
      const index = m.index + m[1].length;
      found.push({ iso: iso(m[order[0]], m[order[1]], m[order[2]]), index, end: m.index + m[0].length });
      if (m[0].length === 0) re.lastIndex += 1;
    }
  }
  found.sort((a, b) => a.index - b.index || b.end - a.end);
  const out = [];
  for (const f of found) {
    if (out.length && f.index < out[out.length - 1].end) continue;
    out.push(f);
  }
  return out.filter(f => f.iso);
};

/** Every label on a line: [{ group, label, start, end }] in reading order. */
const labelsIn = (line) => {
  const out = [];
  LABEL_GROUPS.forEach((re, group) => {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(line)) !== null) {
      // The bare "Date:" label is only the word itself, not the gap or the colon before/after it.
      const word = m[1] || m[0];
      const start = m.index + m[0].indexOf(word);
      out.push({ group, label: word.trim(), start, end: m.index + m[0].length });
      if (m[0].length === 0) re.lastIndex += 1;
    }
  });
  // A longer label that contains a shorter one (e.g. "Collection Date" vs the bare "Date") keeps the first.
  out.sort((a, b) => a.start - b.start || a.group - b.group);
  return out.filter((l, i) => !out.slice(0, i).some(p => l.start < p.end && p.start < l.end));
};

const normaliseDob = (dob) => {
  if (dob instanceof Date && !Number.isNaN(dob.getTime())) return toLocalIsoDate(dob);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(typeof dob === 'string' ? dob : '');
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

const distinct = (isos) => [...new Set(isos)];

/**
 * Blanks the DOB / Age columns of a line (pdfTextLines joins columns with 2+ spaces) and keeps the rest, so
 * "Age/Sex : 46 Y/M    Sample Collected : 04/01/2026" still has its collection date. A label-only column
 * ("DOB :") also blanks the column after it, its value. Blanked with spaces, so column positions hold.
 */
const withoutBirthColumns = (line) => {
  const parts = line.split(/(\s{2,})/); // columns at even indexes, the gaps between them at odd ones
  let blankNext = false;
  for (let i = 0; i < parts.length; i += 2) {
    const birth = BIRTH_COLUMN.test(parts[i]);
    if (birth || blankNext) {
      blankNext = birth && !/\d/.test(parts[i]);
      parts[i] = ' '.repeat(parts[i].length);
    } else {
      blankNext = false;
    }
  }
  return parts.join('');
};

/**
 * Suggests the "date of test" for a report: { date: 'YYYY-MM-DD', source: 'label'|'text'|'filename', label }
 * or null. `text` is the report's text (may be empty), `fileName` its name. Never throws.
 */
export const extractReportDate = (text, fileName, { today = new Date(), dob } = {}) => {
  try {
    const now = today instanceof Date && !Number.isNaN(today.getTime()) ? today : new Date();
    const max = toLocalIsoDate(now);
    const min = `${now.getFullYear() - MAX_AGE_YEARS}${max.slice(4)}`;
    const born = normaliseDob(dob);
    const plausible = (d) => d <= max && d >= min && (!born || d > born);

    const lines = (typeof text === 'string' ? text : '').split(/\r?\n/).map(withoutBirthColumns);

    // 1. Labelled dates: the value is after the label on its own line (up to the next label), or else on
    //    the next line, in the column nearest the label (pdf.js often puts a table's values below it).
    const byGroup = LABEL_GROUPS.map(() => []);
    lines.forEach((line, i) => {
      const labels = labelsIn(line);
      if (!labels.length) return;
      const dates = datesIn(line);
      const next = lines[i + 1] || '';
      const nextDates = labelsIn(next).length ? [] : datesIn(next);
      labels.forEach((l, k) => {
        const stop = k + 1 < labels.length ? labels[k + 1].start : Infinity;
        let hit = dates.find(d => d.index >= l.end && d.index < stop);
        if (!hit && nextDates.length) {
          hit = nextDates.reduce((a, b) => (Math.abs(b.index - l.start) < Math.abs(a.index - l.start) ? b : a));
        }
        if (hit) byGroup[l.group].push({ date: hit.iso, label: l.label });
      });
    });
    // The highest-priority group that has a date decides: one plausible date, or nothing. A rejected
    // (future, too old, birth) or a second, different date in that group means don't guess.
    const group = byGroup.find(g => g.length);
    if (group) {
      const dates = distinct(group.map(g => g.date));
      return dates.length === 1 && plausible(dates[0]) ? { date: dates[0], source: 'label', label: group[0].label } : null;
    }

    // 2. Nothing labelled: one clear date near the top of the report.
    const top = distinct(lines.slice(0, TEXT_FALLBACK_LINES).flatMap(datesIn).map(d => d.iso).filter(plausible));
    if (top.length === 1) return { date: top[0], source: 'text', label: null };

    // 3. The file name, with "_" as a separator (Blood_Test_04-Jan-2026.pdf).
    const base = (typeof fileName === 'string' ? fileName : '').split(/[\\/]/).pop().replace(/\.[a-z0-9]{1,5}$/i, '');
    const named = distinct(datesIn(base.replace(/_+/g, ' ')).map(d => d.iso).filter(plausible));
    if (named.length === 1) return { date: named[0], source: 'filename', label: null };

    return null;
  } catch {
    return null;
  }
};
